import { format as formatDate, isValid, parse, parseISO } from 'date-fns';
import kebabCase from 'lodash/fp/kebabCase';
import lodashIsEmpty from 'lodash/fp/isEmpty';
import { stringify } from 'query-string';
import removeAccents from 'remove-accents';
import { AppConfigService } from '@/config';
import { SentryService } from '@ianesthesie-kit/sentry';
import { roundToPlaces } from '@ianesthesie-kit/core';
import { Region } from '@ianesthesie-kit/services';

export * from './cacheManager';
export * from './content';
export * from './uuid';
export * from './payload';
export * from './asyncOpenInNew';
export * from './practitionerName';

export type PickStartsWith<T extends object, S extends string> = {
  [K in keyof T as K extends `${S}${infer R}` ? K : never]: T[K];
};

export type StartsWith<PREFIX extends string> = `${PREFIX}${string}`;

export type PickEndsWith<T extends object, S extends string> = {
  [K in keyof T as K extends `${infer R}${S}` ? K : never]: T[K];
};

export type EndsWith<SUFFIX extends string> = `${string}${SUFFIX}`;

export type SetTimeout = ReturnType<typeof setTimeout>;

export type SetInterval = ReturnType<typeof setInterval>;

export type GroupedList<E, K extends string, L extends boolean = true> = {
  [key in K]?: L extends true ? E[] : E;
};

export type GroupFunction<E, K extends string, L extends boolean = true> = (
  data: E[]
) => GroupedList<E, K, L>;

// TODO replace usages with singlize from kit
export const singlize = <E, K extends string>(
  gl: GroupedList<E, K>
): GroupedList<E, K, any> => {
  if (!gl) {
    return gl;
  }

  return Object.entries(gl).reduce((acc, [key, list]) => {
    if (Array.isArray(list) && list.length) {
      return {
        ...acc,
        [key]: list[0]
      };
    }

    return acc;
  }, {});
};

export const isEmpty = lodashIsEmpty;
export const isNotEmpty = (val: any) => !isEmpty(val);

export const isNullOrUndefined = (val: any) =>
  val === undefined || val === null;
export const isNotNullNorUndefined = (val: any) => !isNullOrUndefined(val);

export const toKebabQuery = (
  paramObj: object | undefined,
  questionMark = true
) => {
  if (isEmpty(paramObj)) {
    return questionMark ? '?' : '';
  }
  const params = Object.entries(paramObj).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [kebabCase(key)]: val
    }),
    {}
  );
  return `${questionMark ? '?' : ''}${stringify(params)}`;
};

type HasName = {
  firstName: string | undefined;
  lastName: string | undefined;
};
export const getInitials = <T extends HasName | undefined | null>(
  person: T,
  reversed = false
) => {
  if (!person) {
    return undefined;
  }

  const f = person?.firstName?.substring(0, 1).toUpperCase() || '';
  const l = person?.lastName?.substring(0, 1).toUpperCase() || '';
  return reversed ? `${l}${f}` : `${f}${l}`;
};

export function throwOnDev(err: any) {
  if (AppConfigService.getInstance().env === 'dev') {
    throw err;
  }
}

export const captureThrowOnDev = (e: any) => {
  const err = typeof e === 'string' ? new Error(e) : e;
  SentryService.getInstance().captureException(err);
  throwOnDev(err);
};

export const toPercentage = (num = 0, decimals = 0): string =>
  typeof num === 'number' ? `${roundToPlaces(num * 100, decimals)}%` : '-';

export const passthrough = <T>(arg: T): T => arg;

export function runTimes(
  callback: () => void,
  times: number,
  opts: { initialDelayMs?: number; intervalRateMs?: number } = {}
) {
  let timesRun = 0;
  const { initialDelayMs = 0, intervalRateMs = 1000 } = opts;
  const startInterval = () => {
    const interval = setInterval(() => {
      if (timesRun++ >= times) {
        clearInterval(interval);
      } else {
        try {
          callback();
        } catch (e) {
          clearInterval(interval);
        }
      }
    }, intervalRateMs);
  };

  setTimeout(startInterval, initialDelayMs);
}

export const parseBoolean = (str?: any) => {
  if (str === 'true') {
    return true;
  }

  if (str === 'false') {
    return false;
  }

  return undefined;
};

export const prettyPrintRegion = (region: Region | null | undefined) => {
  if (!region) {
    return '';
  }

  return region
    .split('_')
    .map(
      (part) =>
        part.substring(0, 1).toUpperCase() + part.substring(1).toLowerCase()
    )
    .join(' ');
};

/**
 * Normalize string for comparison (case-insensitive + accent-insensitive)
 * @param str - String to normalize
 * @returns Normalized string (lowercase, accents removed, trimmed)
 * @example normalizeForComparison('José') // 'jose'
 * @example normalizeForComparison('MÜLLER') // 'muller'
 */
export const normalizeForComparison = (
  str: string | undefined | null
): string => {
  if (!str) return '';
  return removeAccents(str).toLowerCase().trim();
};

/**
 * Compare two strings in a normalized way (case-insensitive + accent-insensitive)
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns True if strings are equal after normalization
 * @example areStringsEqual('José', 'jose') // true
 * @example areStringsEqual('SMITH', 'smith') // true
 */
export const areStringsEqual = (
  a: string | undefined | null,
  b: string | undefined | null
): boolean => {
  return normalizeForComparison(a) === normalizeForComparison(b);
};
